import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { ArrowLeft, FileText, Crosshair, X, Check, Radio, Users } from 'lucide-react'
import setThemeColor from '../lib/themeColor'
import FragoPhaseStepper from '../components/FragoPhaseStepper'
import useEventRole from '../lib/useEventRole'
import CountdownTimer from '../components/CountdownTimer'
import { TIDDisplaySection } from '../components/TIDSection'
import { FUPSection } from '../components/FUPSection'

// Dossier theme tokens — mirrored from BountyDetail so the FRAGO drill-down
// reads as another file in the same archive.
const D = {
  page: {
    minHeight: '100vh',
    background: '#d8c898',
    color: '#1f1d18',
    fontFamily: "'Special Elite', 'Courier Prime', monospace",
  },
  nav: {
    background: '#1f1d18',
    borderBottom: '3px solid #c08a2a',
    padding: '10px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  paper: {
    background: '#f0e6c8',
    border: '1px solid rgba(120,100,60,.35)',
    boxShadow: '0 2px 8px rgba(0,0,0,.18), 0 1px 3px rgba(0,0,0,.12)',
    position: 'relative',
    overflow: 'hidden',
  },
  paperLines: {
    position: 'absolute', inset: 0, pointerEvents: 'none',
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 26px, rgba(0,0,0,.025) 26px, rgba(0,0,0,.025) 27px)',
  },
  display: { fontFamily: "'Stardos Stencil', 'Special Elite', serif" },
  body: { fontFamily: "'Special Elite', 'Courier Prime', monospace" },
  ink: { color: '#1f1d18' },
  ink2: { color: '#4a4636' },
  ink3: { color: '#7a7058' },
  stamp: { color: '#c08a2a' },
}

const statusStamp = {
  active:    { label: 'IN PROGRESS', color: '#c08a2a' },
  queued:    { label: 'PENDING DISPATCH', color: '#2a4a8c' },
  claimed:   { label: 'MISSION SUCCESS', color: '#3a6a3a' },
  failed:    { label: 'MISSION FAILED', color: '#a82a1f' },
  expired:   { label: 'EXPIRED', color: '#4a4636' },
  cancelled: { label: 'CANCELLED', color: '#4a4636' },
}

function SectionHeader({ num, label }) {
  return (
    <div style={{ ...D.display, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#4a4636', borderBottom: '1px solid #cbb98c', paddingBottom: 3, marginBottom: 8, marginTop: 14 }}>
      § {String(num).padStart(2, '0')} — {label}
    </div>
  )
}

export default function FragoDetail() {
  const { id } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()

  const [bounty, setBounty] = useState(null)
  // ANU-82 — child bounties spawned by the inline builder; surfaced as
  // a dossier section so the GM (and assignees, where the dossier shows
  // up to them) can see the consequences of this FRAGO.
  const [linkedBounties, setLinkedBounties] = useState([])
  const [issuer, setIssuer] = useState(null)
  const [targetFaction, setTargetFaction] = useState(null)
  const [event, setEvent] = useState(null)
  const [assignments, setAssignments] = useState([])
  const [myNodeIds, setMyNodeIds] = useState(new Set())
  const [myLeaderNodeIds, setMyLeaderNodeIds] = useState(new Set())
  const [myChildNodes, setMyChildNodes] = useState([])
  // ANU-61 — viewer's faction in this event. If the FRAGO has a
  // target_faction_id and this doesn't match, the dossier is
  // classified and the page renders a stub instead. GMs and co-GMs
  // bypass the gate (oversight scope).
  const [viewerFactionId, setViewerFactionId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [acting, setActing] = useState(false)
  // FIX D — assignment history rendered from mission_events. Filtered
  // to the handoff event types so the GM can read the chain of who
  // accepted / delegated / declined / aborted.
  const [history, setHistory] = useState([])
  // GM delegation — full event node list so a GM with no personal
  // assignment can pick any node as the delegate. Non-GM viewers
  // keep the chain-restricted myChildNodes picker.
  const [allEventNodes, setAllEventNodes] = useState([])
  // FRAGO Fix 2 + 8 — fog of war. GMs and co-GMs see the full
  // mission structure on this page (operational picture). Regular
  // players get fog-of-war while the FRAGO is in flight, then the
  // full scope is revealed retrospectively once the FRAGO hits a
  // terminal state (claimed = mission success, failed, expired,
  // cancelled) — they should be able to read the whole story after
  // the fact.
  const eventRole = useEventRole(bounty?.event_id)
  const isGmScope = eventRole?.isFullGM || eventRole?.isCoGm
  const isTerminal = bounty && ['claimed','failed','expired','cancelled'].includes(bounty.status)
  const stepperScope = (isGmScope || isTerminal) ? 'full' : 'player'

  useEffect(() => { setThemeColor('dossier') }, [])
  useEffect(() => { fetchAll() }, [id])

  const fetchAll = async () => {
    setLoading(true)
    const { data: b } = await supabase
      .from('bounties')
      .select('*, issuer:users!bounties_issued_by_fkey(id, display_name, callsign)')
      .eq('id', id)
      .limit(1)
    const bounty = b?.[0]
    if (!bounty) { navigate('/play'); return }
    setBounty(bounty)
    setIssuer(bounty.issuer)

    if (bounty.event_id) {
      const [{ data: ev }, { data: fac }, { data: epRows }] = await Promise.all([
        supabase.from('events').select('id, name, status').eq('id', bounty.event_id).limit(1),
        bounty.target_faction_id
          ? supabase.from('event_factions').select('id, name, color').eq('id', bounty.target_faction_id).limit(1)
          : Promise.resolve({ data: [] }),
        supabase.from('event_players')
          .select('faction_id').eq('event_id', bounty.event_id).eq('user_id', user.id).limit(1),
      ])
      setEvent(ev?.[0] || null)
      setTargetFaction(fac?.[0] || null)
      setViewerFactionId(epRows?.[0]?.faction_id || null)
    }

    // ANU-82 — child bounties parented to this FRAGO. GM-only view in
    // the spec, but RLS on bounties already enforces who can see what;
    // the section just renders whatever the viewer can read. Status,
    // target callsign, points, and (where set) an active countdown.
    const { data: lb } = await supabase
      .from('bounties')
      .select('id, title, mission_name, bounty_type, points, status, expires_at, active_duration, linked_trigger, target_id, target:users!bounties_target_id_fkey(callsign, display_name)')
      .eq('parent_frago_id', id)
      .order('issued_at', { ascending: true })
    setLinkedBounties(lb || [])

    // Assignments + nodes + assigner
    const { data: as } = await supabase
      .from('frago_assignments')
      .select('*, node:mcs_nodes(id, name, callsign, faction_id, parent_id, assigned_player_id, radio_freq, team:teams(id, name)), assigner:users!frago_assignments_assigned_by_fkey(callsign, display_name)')
      .eq('bounty_id', id)
      .order('assigned_at', { ascending: true })
    setAssignments(as || [])

    // FIX D — handoff history for the ASSIGNMENT HISTORY section.
    // Filters mission_events to delegation lifecycle event types so
    // the chain renders cleanly without bounty / kill noise.
    const { data: ev } = await supabase
      .from('mission_events')
      .select('id, event_type, notes, triggered_by, created_at, triggered_by_user:users!mission_events_triggered_by_fkey(callsign, display_name)')
      .eq('bounty_id', id)
      .in('event_type', ['frago_issued','frago_accepted','frago_delegated','frago_declined','frago_returned','frago_redelegated','frago_aborted','frago_completed','frago_failed','frago_expired'])
      .order('created_at', { ascending: true })
    setHistory(ev || [])

    // Identify the player's node memberships in this event so the action
    // bar can decide which buttons to show. Fetch every node in the event
    // once, then check memberships against that set — sidesteps the
    // mcs_nodes!inner join which has bitten us before under RLS.
    if (bounty.event_id) {
      const { data: eventNodes } = await supabase
        .from('mcs_nodes')
        .select('id, name, callsign, parent_id, faction_id, assigned_player_id, node_type')
        .eq('event_id', bounty.event_id)
      setAllEventNodes(eventNodes || [])
      const eventNodeIds = (eventNodes || []).map(n => n.id)

      const [{ data: memberRows }, { data: leaderRows }] = await Promise.all([
        eventNodeIds.length
          ? supabase.from('mcs_node_members')
              .select('node_id').eq('user_id', user.id).in('node_id', eventNodeIds)
          : Promise.resolve({ data: [] }),
        supabase.from('mcs_nodes')
          .select('id').eq('event_id', bounty.event_id).eq('assigned_player_id', user.id),
      ])
      const memberNodeIds = (memberRows || []).map(r => r.node_id)
      const leaderNodeIds = (leaderRows || []).map(n => n.id)
      const allMyNodeIds = Array.from(new Set([...memberNodeIds, ...leaderNodeIds]))
      setMyNodeIds(new Set(allMyNodeIds))
      setMyLeaderNodeIds(new Set(leaderNodeIds))
      // Children of any node the viewer belongs to in this event — used by
      // the DELEGATE picker. Computed client-side from the eventNodes pull
      // so an extra round-trip isn't needed.
      const kids = (eventNodes || []).filter(n => n.parent_id && allMyNodeIds.includes(n.parent_id))
      setMyChildNodes(kids)
    }

    setLoading(false)
  }

  const myPursuit = assignments.find(a => a.status === 'pursuing' && a.assigned_by === user.id)
  // Status-aware action gating. The leader on the assigned node has:
  //   pending / active  → ACCEPT, DECLINE, DELEGATE
  //   accepted          → ABORT only
  //   delegated         → no actions (read-only "⊳ DELEGATED" badge)
  // 'active' is the legacy synonym for 'pending' that lives on older
  // rows; both are treated the same. Accept is a commitment — if the
  // leader wants to redirect a sub-task after accepting, they ABORT
  // and the parent re-delegates (operator correction 2026-05-16).
  const myPendingAssignment = assignments.find(
    a => (a.status === 'pending' || a.status === 'active') && myNodeIds.has(a.node_id)
  )
  const myAcceptedAssignmentAsLeader = assignments.find(
    a => a.status === 'accepted' && myNodeIds.has(a.node_id)
  )
  const myDelegatedAssignmentAsLeader = assignments.find(
    a => a.status === 'delegated' && myNodeIds.has(a.node_id)
  )
  // DELEGATE picker only renders for pending assignments. GMs keep
  // the separate "DELEGATE (GM) TO NODE" picker for the no-parent
  // direct-assign path.
  const canDelegate = !!myPendingAssignment
  const isBroadcast = bounty?.target_type === 'faction'
  const isActive = bounty?.status === 'active'

  const pursue = async () => {
    setActing(true)
    let nodeId = [...myLeaderNodeIds][0] || null
    if (!nodeId) nodeId = [...myNodeIds][0] || null
    if (!nodeId) { alert('You must be assigned to an MCS node to pursue a FRAGO.'); setActing(false); return }
    const { error } = await supabase.from('frago_assignments').insert({
      bounty_id: id, node_id: nodeId, assigned_by: user.id, status: 'pursuing',
    })
    if (error) alert('Pursue failed: ' + error.message)
    await fetchAll()
    setActing(false)
  }

  const standDown = async () => {
    if (!myPursuit) return
    setActing(true)
    await supabase.from('frago_assignments').delete().eq('id', myPursuit.id)
    await fetchAll()
    setActing(false)
  }

  // SEC 2 — accept/decline/abort all route through SECURITY DEFINER
  // RPCs now so the parent chain logic (delegated → accepted on
  // decline/abort, mission_events audit, etc.) stays atomic.
  const accept = async (assignmentId) => {
    setActing(true)
    const { data, error } = await supabase.rpc('frago_accept_assignment', { p_assignment_id: assignmentId })
    if (error || data?.ok === false) alert('Accept failed: ' + (error?.message || data?.reason || 'unknown'))
    await fetchAll()
    setActing(false)
  }

  const decline = async (assignmentId) => {
    if (!window.confirm('Decline this FRAGO assignment? It returns to whoever delegated it.')) return
    setActing(true)
    const { data, error } = await supabase.rpc('frago_decline_assignment', { p_assignment_id: assignmentId })
    if (error || data?.ok === false) alert('Decline failed: ' + (error?.message || data?.reason || 'unknown'))
    await fetchAll()
    setActing(false)
  }

  // FIX A's ABORT — for mid-mission assignments already accepted.
  const abort = async (assignmentId) => {
    if (!window.confirm('Abort this mission? It will be returned to whoever delegated it. Completed objectives are preserved.')) return
    setActing(true)
    const { data, error } = await supabase.rpc('frago_abort_assignment', { p_assignment_id: assignmentId })
    if (error || data?.ok === false) alert('Abort failed: ' + (error?.message || data?.reason || 'unknown'))
    await fetchAll()
    setActing(false)
  }

  // Find the viewer's pending assignment that "owns" this delegation —
  // used by delegateToChild to flip the parent row to 'delegated'.
  // Accepted rows are NOT delegatable per the operator correction: a
  // leader who has committed must ABORT first; the parent then
  // re-delegates. If the viewer has no pending row, delegateToChild
  // falls through to the GM no-parent path.
  const myDelegatableAssignment = (assignmentsList) => {
    const inMyNode = (a) => myNodeIds.has(a.node_id)
    return (
      assignmentsList.find(a => inMyNode(a) && a.status === 'pending')
      || assignmentsList.find(a => inMyNode(a) && a.status === 'active')
      || null
    )
  }

  const delegateToChild = async (childNodeId) => {
    if (!childNodeId) return
    const parent = myDelegatableAssignment(assignments)
    setActing(true)
    // FIX C + GM-direct delegation:
    // - When the caller holds an active assignment (chain path),
    //   pass p_parent_assignment_id so the parent flips to
    //   'delegated' and the chain stays linked.
    // - When the caller has no assignment (GM acting on a
    //   broadcast FRAGO or yanking the chain), pass p_bounty_id
    //   instead. The server-side GM-tier check authorises the
    //   no-parent path; the child gets a fresh pending row.
    const args = parent
      ? { p_parent_assignment_id: parent.id, p_child_node_id: childNodeId }
      : { p_bounty_id: id, p_child_node_id: childNodeId }
    const { data, error } = await supabase.rpc('frago_delegate_assignment', args)
    if (error || data?.ok === false) {
      alert('Delegate failed: ' + (error?.message || data?.reason || 'unknown'))
    }
    await fetchAll()
    setActing(false)
  }

  if (loading) return (
    <div style={{ ...D.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ ...D.display, fontSize: 14, color: '#7a7058', letterSpacing: '0.14em' }}>RETRIEVING FRAGO...</p>
    </div>
  )
  if (!bounty) return null

  // ANU-61 — dossier faction gate. A FRAGO with a target_faction_id
  // is classified to that faction. Any viewer whose event faction
  // doesn't match (and who isn't a GM / co-GM) gets a stub instead of
  // the briefing. Pairs with Notifications.handleTap dropping nav for
  // opposing_alert + the trigger stripping frago_id from those rows.
  const factionLocked = !!bounty.target_faction_id
    && !isGmScope
    && viewerFactionId !== bounty.target_faction_id
  if (factionLocked) return (
    <div style={D.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Stardos+Stencil:wght@400;700&family=Special+Elite&display=swap');`}</style>
      <div style={D.nav}>
        <div>
          <div style={{ ...D.display, color: '#f0e6c8', fontSize: 14, letterSpacing: '0.14em' }}>ANUBIS</div>
          <div style={{ color: '#c08a2a', fontSize: 9, letterSpacing: '0.12em', marginTop: 2 }}>FIELD OPS · FRAGO BRANCH</div>
        </div>
        <div style={{ color: '#c08a2a', fontSize: 9, letterSpacing: '0.08em' }}>
          {event?.name?.toUpperCase() || 'NO EVENT'}
        </div>
      </div>
      <button onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'none', border: 'none', color: '#7a7058', fontFamily: "'Special Elite', monospace", fontSize: 11, cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        <ArrowLeft size={14} /> RETURN TO FILES
      </button>
      <div style={{ margin: '0 10px 16px', ...D.paper, padding: '32px 20px', textAlign: 'center' }}>
        <div style={D.paperLines} />
        <div style={{ ...D.display, fontSize: 28, color: '#a82a1f', letterSpacing: '0.18em', marginBottom: 10 }}>
          ▣ CLASSIFIED
        </div>
        <div style={{ ...D.body, fontSize: 13, color: '#4a4636', maxWidth: 360, margin: '0 auto', lineHeight: 1.6 }}>
          Intelligence suggests enemy activity in this sector.
        </div>
        <div style={{ ...D.body, fontSize: 11, color: '#7a7058', marginTop: 14, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          File not authorised for your faction.
        </div>
      </div>
    </div>
  )

  const stamp = statusStamp[bounty.status] || statusStamp.active
  const fileNum = `FR-${new Date(bounty.issued_at).getFullYear()}-${bounty.id.slice(0, 4).toUpperCase()}`
  const expiresAt = bounty.expires_at ? new Date(bounty.expires_at) : null
  const msLeft = expiresAt ? expiresAt - Date.now() : null
  const minLeft = msLeft != null ? Math.ceil(msLeft / 60000) : null

  let sectionNum = 0
  const sn = () => ++sectionNum

  return (
    <div style={D.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Stardos+Stencil:wght@400;700&family=Special+Elite&family=Courier+Prime:wght@400;700&display=swap');`}</style>

      {/* NAV */}
      <div style={D.nav}>
        <div>
          <div style={{ ...D.display, color: '#f0e6c8', fontSize: 14, letterSpacing: '0.14em' }}>ANUBIS</div>
          <div style={{ color: '#c08a2a', fontSize: 9, letterSpacing: '0.12em', marginTop: 2 }}>FIELD OPS · FRAGO BRANCH</div>
        </div>
        <div style={{ color: '#c08a2a', fontSize: 9, letterSpacing: '0.08em' }}>
          {event?.name?.toUpperCase() || 'NO EVENT'}
        </div>
      </div>

      <button onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'none', border: 'none', color: '#7a7058', fontFamily: "'Special Elite', monospace", fontSize: 11, cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        <ArrowLeft size={14} /> RETURN TO FILES
      </button>

      <div style={{ margin: '0 10px 16px', ...D.paper }}>
        <div style={D.paperLines} />

        {/* Header strip */}
        <div style={{ padding: '14px 16px', borderBottom: '2px solid #c08a2a', position: 'relative' }}>
          <div className="flex justify-between items-start gap-3">
            <div>
              <div style={{ ...D.display, fontSize: 22, color: '#1f1d18', letterSpacing: '0.06em' }}>
                <FileText size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 8, color: '#c08a2a' }} />
                {bounty.mission_name || 'UNNAMED MISSION'}
              </div>
              <div style={{ ...D.body, fontSize: 10, color: '#7a7058', marginTop: 4, letterSpacing: '0.08em' }}>
                FILE {fileNum} · ISSUED {new Date(bounty.issued_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ ...D.display, fontSize: 32, color: '#c08a2a', lineHeight: 1 }}>{bounty.points}</div>
              <div style={{ ...D.body, fontSize: 9, color: '#7a7058', letterSpacing: '0.14em', textTransform: 'uppercase' }}>PTS</div>
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <span style={{ ...D.display, ...D.stamp, fontSize: 12, letterSpacing: '0.18em', padding: '3px 10px', border: `2px solid ${stamp.color}`, color: stamp.color }}>
              ● {stamp.label}
            </span>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 16px', position: 'relative' }}>
          {/* Briefing */}
          <SectionHeader num={sn()} label="BRIEFING / OBJECTIVES" />
          <p style={{ ...D.body, fontSize: 13, color: '#1f1d18', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {bounty.notes || <span style={D.ink3}>No briefing on file.</span>}
          </p>

          {/* ── TARGET IDENTIFICATION (ANU-88) ── */}
          {bounty && (
            <TIDDisplaySection
              parentType="frago"
              parentId={bounty.id}
              eventId={bounty.event_id}
              sectionNum={sn()}
            />
          )}

          {/* ── FIELD UPDATES (ANU-132) ── */}
          {bounty && (
            <FUPSection
              parentType="frago"
              parentId={bounty.id}
              eventId={bounty.event_id}
              sectionNum={sn()}
            />
          )}

          {/* Phase 5 of the FRAGO Rework — structured phase stepper.
              Renders nothing for legacy FRAGOs that pre-date the rework
              (frago_phases lookup returns empty). For new-schema FRAGOs
              this is the canonical mission map. */}
          <div style={{ marginTop: 14 }}>
            <SectionHeader num={sn()} label="MISSION STRUCTURE" />
            <FragoPhaseStepper fragoId={bounty.id} viewerScope={stepperScope} />
          </div>

          {/* ANU-82 — Linked bounties. Read-only here; lifecycle is
              owned by the parent FRAGO's cascade triggers (the ANU-81
              lock hides manual activate / cancel everywhere else). */}
          {linkedBounties.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <SectionHeader num={sn()} label="LINKED BOUNTIES" />
              <div style={{ ...D.body, fontSize: 12, lineHeight: 1.55 }}>
                {linkedBounties.map(lb => {
                  const t = (lb.bounty_type || '').toUpperCase()
                  const targetName = lb.target?.callsign || lb.target?.display_name || '—'
                  const lt = lb.linked_trigger || {}
                  const activateLabel = lt.activate === 'with_frago'
                    ? 'with FRAGO'
                    : lt.activate === 'on_phase'
                      ? 'on phase'
                      : lt.activate === 'on_objective'
                        ? 'on objective'
                        : (lt.activate || '—')
                  const statusColor = ({
                    active: '#3a6e3a',
                    queued: '#c08a2a',
                    claimed: '#3a6e3a',
                    cancelled: '#7a7058',
                    expired: '#7a7058',
                    failed: '#a82a1f',
                  })[lb.status] || '#4a4636'
                  return (
                    <div key={lb.id} style={{ borderBottom: '1px dashed #cbb98c', padding: '6px 0', display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span style={{ ...D.display, fontSize: 10, letterSpacing: '0.12em', color: '#c08a2a', minWidth: 60 }}>{t}</span>
                      <span style={{ flex: 1, minWidth: 120, color: '#1f1d18' }}>
                        {lb.title?.trim() || 'KILL'} — {targetName || `B${(lb.id || '???').slice(0,3).toUpperCase()}`}
                        <span style={D.ink3}> → </span>
                        <strong>{targetName}</strong>
                      </span>
                      <span style={{ ...D.body, fontSize: 10, color: '#7a7058' }}>{lb.points} pts · activate {activateLabel}</span>
                      <span style={{ ...D.display, fontSize: 10, color: statusColor, letterSpacing: '0.12em' }}>{(lb.status || '').toUpperCase()}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* FIX D — Assignment history. Renders mission_events for
              the FRAGO's delegation lifecycle so the GM can see the
              chain (delegated → declined → re-delegated → accepted
              → aborted etc.) at a glance. Critical for situational
              awareness when a mission stalls. */}
          {history.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <SectionHeader num={sn()} label="ASSIGNMENT HISTORY" />
              <div style={{ ...D.body, fontSize: 12, lineHeight: 1.6 }}>
                {history.map(h => {
                  const who = h.triggered_by_user?.callsign || h.triggered_by_user?.display_name
                  const when = h.created_at
                    ? new Date(h.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                    : ''
                  const verb = ({
                    frago_issued:      'Activated',
                    frago_accepted:    'Accepted',
                    frago_delegated:   'Delegated',
                    frago_declined:    'Declined',
                    frago_returned:    'Returned',
                    frago_redelegated: 'Re-delegated',
                    frago_aborted:     'Aborted',
                    frago_completed:   'Completed',
                    frago_failed:      'Failed',
                    frago_expired:     'Expired',
                  })[h.event_type] || h.event_type
                  return (
                    <div key={h.id} style={{ borderBottom: '1px dashed #cbb98c', padding: '4px 0', display: 'flex', gap: 10, alignItems: 'baseline' }}>
                      <span style={{ ...D.body, fontSize: 10, color: '#7a7058', minWidth: 90 }}>{when}</span>
                      <span style={{ ...D.display, fontSize: 10, letterSpacing: '0.12em', color: '#c08a2a', minWidth: 110 }}>{verb.toUpperCase()}</span>
                      <span style={{ flex: 1, color: '#1f1d18' }}>
                        {who ? <><span style={D.ink3}>by </span>{who}</> : null}
                        {h.notes ? <span style={{ color: '#4a4636' }}>{who ? ' · ' : ''}{h.notes}</span> : null}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Assignment */}
          <SectionHeader num={sn()} label="ASSIGNMENT" />
          <div style={{ ...D.body, fontSize: 12, color: '#1f1d18', lineHeight: 1.6 }}>
            <div><span style={D.ink3}>ISSUED BY: </span>{issuer?.callsign || issuer?.display_name || 'Command'}</div>
            <div>
              <span style={D.ink3}>DISPATCH: </span>
              {isBroadcast
                ? <>BROADCAST → {targetFaction?.name || 'faction'}</>
                : <>DELEGATED ({assignments.length} assignment{assignments.length === 1 ? '' : 's'})</>}
            </div>
            {assignments.length > 0 && (
              <div style={{ marginTop: 10, paddingLeft: 12, borderLeft: '2px solid #cbb98c' }}>
                {assignments.map(a => (
                  <div key={a.id} style={{ ...D.body, fontSize: 11, color: '#4a4636', padding: '3px 0' }}>
                    <Users size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6, color: '#7a7058' }} />
                    {a.node?.callsign ? `${a.node.callsign} — ${a.node.name}` : a.node?.name || a.node_id?.slice(0, 8)}
                    {a.node?.team?.name && <span style={{ marginLeft: 6, color: '#3a6a3a' }}>· ◇ {a.node.team.name}</span>}
                    {a.node?.radio_freq && <span style={{ marginLeft: 6, color: '#2a4a8c' }}>· <Radio size={9} style={{ display: 'inline', verticalAlign: 'middle' }} /> {a.node.radio_freq}</span>}
                    <span style={{ marginLeft: 8, color: a.status === 'active' ? '#c08a2a' : a.status === 'completed' || a.status === 'accepted' ? '#3a6a3a' : a.status === 'failed' ? '#a82a1f' : a.status === 'pursuing' ? '#2a4a8c' : '#7a7058', textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.08em' }}>
                      · {a.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Timer */}
          {expiresAt && (
            <>
              <SectionHeader num={sn()} label="TIMER" />
              <div style={{ ...D.body, fontSize: 13, color: msLeft > 0 ? '#c08a2a' : '#a82a1f', lineHeight: 1.5 }}>
                {msLeft > 0
                  ? <>Expires in <strong><CountdownTimer expiresAt={bounty.expires_at} prefix="" /></strong> ({expiresAt.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })})</>
                  : <>Expired {expiresAt.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</>}
              </div>
            </>
          )}

          {/* Actions */}
          <SectionHeader num={sn()} label="ACTIONS" />
          <div className="flex flex-wrap gap-2" style={{ marginTop: 4 }}>
            {/* Broadcast: pursue / stand-down */}
            {isBroadcast && isActive && !myPursuit && (
              <button onClick={pursue} disabled={acting} style={btn('#2a4a8c', '#f0e6c8')}>
                <Crosshair size={12} /> PURSUE
              </button>
            )}
            {isBroadcast && isActive && myPursuit && (
              <button onClick={standDown} disabled={acting} style={btn('#a82a1f', '#f0e6c8')}>
                <X size={12} /> STAND DOWN
              </button>
            )}

            {/* DELEGATED badge — viewer's row has been pushed down-tree */}
            {!isBroadcast && myDelegatedAssignmentAsLeader && (
              <span style={{ ...D.body, fontSize: 11, color: '#7a7058', letterSpacing: '0.08em', textTransform: 'uppercase', alignSelf: 'center' }}>
                ⊳ DELEGATED — {myDelegatedAssignmentAsLeader.node?.callsign || myDelegatedAssignmentAsLeader.node?.name}
              </span>
            )}

            {/* Pending on my node and not yet accepted */}
            {!isBroadcast && isActive && myPendingAssignment && !myDelegatedAssignmentAsLeader && (
              <>
                <button onClick={() => accept(myPendingAssignment.id)} disabled={acting} style={btn('#3a6a3a', '#f0e6c8')}>
                  <Check size={12} /> ACCEPT
                </button>
                <button onClick={() => decline(myPendingAssignment.id)} disabled={acting} style={btn('#a82a1f', '#f0e6c8')}>
                  <X size={12} /> DECLINE
                </button>
              </>
            )}

            {/* Accepted as leader — ABORT button + status label */}
            {!isBroadcast && isActive && myAcceptedAssignmentAsLeader && (
              <>
                <span style={{ ...D.body, fontSize: 11, color: '#3a6a3a', letterSpacing: '0.08em', textTransform: 'uppercase', alignSelf: 'center' }}>
                  ✓ ACCEPTED — {myAcceptedAssignmentAsLeader.node?.callsign || myAcceptedAssignmentAsLeader.node?.name}
                </span>
                <button onClick={() => abort(myAcceptedAssignmentAsLeader.id)} disabled={acting} style={btn('#a82a1f', '#f0e6c8')}>
                  <X size={12} /> ABORT
                </button>
              </>
            )}

            {/* GM delegation — show ALL nodes (the no-parent RPC
                path lets a GM direct-assign to any node, bypassing
                the chain). Non-GM viewers keep the chain-restricted
                myChildNodes picker, AND only get it when they hold
                a pending or accepted assignment on their own node. */}
            {isActive && isGmScope && allEventNodes.length > 0 && (
              <select onChange={e => { delegateToChild(e.target.value); e.target.value = '' }} value="" disabled={acting}
                style={{
                  ...D.body, fontSize: 11, padding: '6px 10px',
                  background: '#f0e6c8', border: '1px solid #cbb98c', color: '#1f1d18',
                  letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
                }}>
                <option value="">⊳ DELEGATE (GM) TO NODE...</option>
                {allEventNodes.map(n => (
                  <option key={n.id} value={n.id}>
                    {n.callsign ? `${n.callsign} — ${n.name}` : n.name}
                  </option>
                ))}
              </select>
            )}
            {/* Non-GM chain delegation: only when the viewer holds a
                pending or accepted assignment on their own node AND
                their node has direct child nodes to hand off to. */}
            {isActive && !isGmScope && canDelegate && myChildNodes.length > 0 && (
              <select onChange={e => { delegateToChild(e.target.value); e.target.value = '' }} value="" disabled={acting}
                style={{
                  ...D.body, fontSize: 11, padding: '6px 10px',
                  background: '#f0e6c8', border: '1px solid #cbb98c', color: '#1f1d18',
                  letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
                }}>
                <option value="">⊳ DELEGATE TO CHILD NODE...</option>
                {myChildNodes.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.callsign ? `${c.callsign} — ${c.name}` : c.name}
                  </option>
                ))}
              </select>
            )}

            {!isActive && (
              <span style={{ ...D.body, fontSize: 11, color: '#7a7058', alignSelf: 'center' }}>
                No actions — FRAGO is {bounty.status}.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function btn(bg, fg) {
  return {
    fontFamily: "'Stardos Stencil', 'Special Elite', serif",
    fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
    background: bg, color: fg, border: 'none', padding: '8px 14px',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
  }
}
